import React, { PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { DndSortableInternalContext } from './context';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { SortableObjectSchema } from './utils';
import { IDndSortablePrivateContext } from './types';

interface IDndSortableProps {
  onDrop: (sourceIndex: number, destinationIndex: number) => void;
  disabled: boolean;
  overlay: ReactNode;
  items: string[];
  onDragStart?: (event: DragStartEvent) => void;
}

/**
 * A Kubit-specific implementation of a dnd-kit/sortable utilizing a horizontal sortable list
 * The sortable list contains a list of items, user can rearrange the items in the same row.
 *
 * @param onDrop - a handler invoked when user drops an item. It is only invoked if the item is dropped _in_
 * the grid (never - when outside) and that `canDrop` (see below) allowed dropping at that position
 * @param children - <MultiGrid> components
 * @param disabled - if true, drag and drop is disabled
 */

export const DndSortable = ({
  onDrop,
  onDragStart,
  children,
  disabled,
  overlay,
  items,
}: PropsWithChildren<IDndSortableProps>) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [currentDragHandle, setCurrentDragHandle] =
    useState<UniqueIdentifier | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    onDragStart?.(event);
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    const activeSortable = active.data.current?.sortable;
    const overSortable = over?.data.current?.sortable;
    if (activeSortable && overSortable) {
      const parsedActive = SortableObjectSchema.parse(activeSortable);
      const parsedOver = SortableObjectSchema.parse(overSortable);
      onDrop(parsedActive.index, parsedOver.index);
    }

    setIsDragging(false);
    setCurrentDragHandle(event.active.id);
  };

  // use context to pass DnD context (what is currently dragging, can you drop, etc.) to avoid prop-drilling
  // when the subcomponents need to know or modify the DnD state
  const contextValue = useMemo<IDndSortablePrivateContext>(
    () => ({
      currentDragHandle,
      setCurrentDragHandle,
      disabled,
    }),
    [currentDragHandle, disabled],
  );

  const sensors = useSensors(useSensor(PointerSensor));

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      collisionDetection={closestCenter}
    >
      <DndSortableInternalContext.Provider value={contextValue}>
        <SortableContext items={items} strategy={horizontalListSortingStrategy}>
          {children}
        </SortableContext>

        {isDragging && <DragOverlay>{overlay}</DragOverlay>}
      </DndSortableInternalContext.Provider>
    </DndContext>
  );
};

export default DndSortable;
